import { useMemo, useState, type FormEvent } from 'react';
import { useSettings } from '../../hooks/useSettings';
import { DEFAULT_PURCHASE_COLUMNS, PURCHASE_COLUMNS, chosenColumns } from '../../utils/documentColumns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useLines } from '../../hooks/useLines';
import { useMasters } from '../../hooks/useMasters';
import { date, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { RatePanel } from '../../components/RatePanel';
import { useToast } from '../../components/ui/Toast';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { usePermission } from '../../hooks/usePermission';
import { DateInput, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { CellInput, CellSelect, LineRow, LineTable } from '../../components/ui/LineTable';
import { QuickProduct } from '../../components/products/QuickProduct';
import { PhotoCell } from '../../components/ui/PhotoCell';
import { useLocations } from '../../hooks/useLocations';
import { MasterLink } from '../../components/ui/MasterLink';

/** What the detail route returns, as far as re-opening an entry needs it. */
interface PurchaseDetail {
  supplier_id: string | null;
  advance_used?: string | null;
  branch_id: string | null;
  doc_date: string;
  supplier_invoice_no: string | null;
  supplier_invoice_date: string | null;
  purchase_type: string | null;
  is_interstate: boolean | null;
  tax_total: string | null;
  settlement: 'CREDIT' | 'CASH' | 'BANK' | null;
  tax_pct: string | null;
  lines: {
    product_id: string | null;
    purity_id: string;
    gross_wt: string;
    stone_wt: string;
    line_total: string;
    /** Exactly what the line was entered with. */
    calc_input: {
      cost?: string;
      ratePerGram?: string;
      rateBasis?: 'NET' | 'FINE';
      wastage?: { mode: 'PCT' | 'AMOUNT'; value: string };
      making?: { mode: 'PER_GRAM' | 'AMOUNT' | 'PCT'; value: string };
      stoneAmount?: string;
      imageDataUri?: string;
      locationId?: string;
    } | null;
  }[];
}

interface PurchaseRow {
  id: string;
  doc_no: string;
  doc_date: string;
  purchase_type: string;
  party_name: string;
  grand_total: string;
  status: string;
  replaced_by_doc_no: string | null;
  replaces_doc_no: string | null;
  pieces: number;
}

interface Line {
  productId: string;
  itemCode: string;
  barcode: string;
  purityId: string;
  grossWt: string;
  stoneWt: string;
  cost: string;
  /** Or the terms the cost is worked out from, as a supplier's bill reads. */
  ratePerGram: string;
  rateBasis: 'NET' | 'FINE';
  wastageMode: 'PCT' | 'AMOUNT';
  wastageValue: string;
  makingMode: 'PER_GRAM' | 'AMOUNT' | 'PCT';
  makingValue: string;
  stoneAmount: string;
  /** A photo of this piece, taken as it is unpacked. */
  photo: string;
  /** Blank means "wherever the rest of this purchase goes". */
  locationId: string;
}

const blankLine = (): Line => ({
  productId: '',
  itemCode: '',
  barcode: '',
  purityId: '',
  grossWt: '',
  stoneWt: '',
  cost: '',
  ratePerGram: '',
  rateBasis: 'NET',
  wastageMode: 'PCT',
  wastageValue: '',
  makingMode: 'PER_GRAM',
  makingValue: '',
  stoneAmount: '',
  photo: '',
  locationId: '',
});

export function PurchaseEntryPage() {
  const client = useQueryClient();
  const { can } = usePermission();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const masters = useMasters();
  const { branches, defaultBranchId } = useBranches();
  const lines = useLines<Line>(blankLine);

  const [open, setOpen] = useState(false);
  const [quickFor, setQuickFor] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<{ id: string; doc_no: string } | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  /**
   * The posted purchase this entry will replace when saved. Nothing happens to
   * it until then: saving voids it and posts the corrected one together, so a
   * save that fails leaves the original exactly as it was.
   */
  const [correcting, setCorrecting] = useState<{ id: string; doc_no: string } | null>(null);
  /** The advance the purchase being corrected used; it is freed when that one is replaced. */
  const [correctingAdvance, setCorrectingAdvance] = useState<{ supplierId: string; amount: number }>({
    supplierId: '',
    amount: 0,
  });
  /** A cancelled purchase being entered again in its place. */
  const [reentering, setReentering] = useState<{ id: string; doc_no: string } | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [header, setHeader] = useState({
    supplierId: '',
    docDate: today(),
    supplierInvoiceNo: '',
    supplierInvoiceDate: '',
    purchaseType: 'JEWELLERY',
    settlement: 'CREDIT',
    useAdvance: true,
    isInterstate: false,
    taxAmount: '',
    taxMode: 'PCT' as 'PCT' | 'AMOUNT',
    taxPct: '',
    branchId: '',
    locationId: '',
  });

  const activeBranchId = header.branchId || defaultBranchId || '';
  const { locations } = useLocations(activeBranchId);
  const locationOptions = locations.map((l) => ({ value: l.id, label: l.name }));

  const list = useQuery({
    queryKey: ['purchases'],
    queryFn: () => api.get<{ data: PurchaseRow[] }>('/purchases'),
  });
  const suppliers = useQuery({ queryKey: ['suppliers', ''], queryFn: () => endpoints.parties.suppliers() });
  const products = useQuery({ queryKey: ['products', ''], queryFn: () => endpoints.masters.products() });

  // Retired suppliers keep their history but are not offered on a new purchase.
  const supplierList = (
    (suppliers.data?.data ?? []) as unknown as {
      id: string;
      name: string;
      is_registered: boolean;
      is_active?: boolean;
    }[]
  ).filter((s) => s.is_active !== false);
  const productList = (products.data?.data ?? []) as unknown as {
    id: string;
    sku: string;
    name: string;
    metal_id: string;
    default_purity_id?: string;
    image_data_uri?: string | null;
  }[];

  const chosenSupplier = supplierList.find((s) => s.id === header.supplierId);

  /** Every piece costs something; the header total is what the supplier is owed. */
  /**
   * What a piece costs, worked out exactly as the server will.
   *
   * The screen showing one figure and the books recording another is the kind
   * of difference nobody notices until a profit report looks wrong, so this
   * mirrors the server's own sum rather than approximating it.
   */
  // Which columns this shop captures when buying (Settings → Columns when
  // buying). Product, purity, gross weight and cost are always there.
  const { setting } = useSettings();
  const shown = new Set(
    chosenColumns(setting('documents.purchase_columns', DEFAULT_PURCHASE_COLUMNS), PURCHASE_COLUMNS).map((c) => c.key),
  );
  const has = (key: string): boolean => shown.has(key);
  const headings: { key: string; label: string }[] = [
    { key: 'photo', label: 'Photo' },
    { key: 'product', label: 'Product' },
    { key: 'tag', label: 'Tag no. (optional)' },
    { key: 'barcode', label: 'Barcode' },
    { key: 'purity', label: 'Purity' },
    { key: 'grossWt', label: 'Gross wt' },
    { key: 'stoneWt', label: 'Stone wt' },
    { key: 'netWt', label: 'Net wt' },
    { key: 'rate', label: 'Rate/g' },
    { key: 'wastage', label: 'Wastage' },
    { key: 'making', label: 'Making' },
    { key: 'stoneAmount', label: 'Stones ₹' },
    { key: 'cost', label: 'Cost' },
    { key: 'place', label: 'Place' },
  ].filter((h) => has(h.key));
  const beforeCost = headings.findIndex((h) => h.key === 'cost');
  const afterCost = headings.length - beforeCost - 1;

  const lineCost = (l: Line): number => {
    if (l.cost) return Number(l.cost);
    const purity = masters.purities.find((p) => p.id === l.purityId);
    const net = Number(l.grossWt || 0) - Number(l.stoneWt || 0);
    const weighed = l.rateBasis === 'FINE' ? net * Number(purity?.fineness ?? 0) : net;
    const metal = Number(l.ratePerGram || 0) * weighed;
    const wastage = l.wastageMode === 'PCT' ? (metal * Number(l.wastageValue || 0)) / 100 : Number(l.wastageValue || 0);
    const making =
      l.makingMode === 'PER_GRAM'
        ? net * Number(l.makingValue || 0)
        : l.makingMode === 'PCT'
          ? (metal * Number(l.makingValue || 0)) / 100
          : Number(l.makingValue || 0);
    return metal + wastage + making + Number(l.stoneAmount || 0);
  };

  const itemTotal = lines.values.reduce((a, l) => a + lineCost(l), 0);
  const taxOnBill =
    header.taxMode === 'PCT' ? (itemTotal * Number(header.taxPct || 0)) / 100 : Number(header.taxAmount || 0);
  const grandTotal = itemTotal + taxOnBill;

  // What this supplier already holds of the shop's money, paid ahead. It pays
  // the bill first unless unticked; the rest is settled as chosen.
  const supplierAdvance = useQuery({
    queryKey: ['advances', 'balance', 'SUPPLIER', header.supplierId],
    queryFn: () => api.get<{ balance: string }>(`/advances/balance/SUPPLIER/${header.supplierId}`),
    enabled: header.supplierId !== '',
  });
  const advanceHeld = header.supplierId
    ? Number(supplierAdvance.data?.balance ?? 0) +
      (correcting && correctingAdvance.supplierId === header.supplierId ? correctingAdvance.amount : 0)
    : 0;
  const advanceUsed = header.useAdvance && advanceHeld > 0 ? Math.min(advanceHeld, grandTotal) : 0;
  const afterAdvance = Math.max(0, grandTotal - advanceUsed);

  const purityOptionsFor = useMemo(
    () => (productId: string) => {
      const product = productList.find((p) => p.id === productId);
      if (!product) return [];
      return masters.puritiesFor(product.metal_id).map((p) => ({ value: p.id, label: p.code }));
    },
    [productList, masters],
  );

  const create = useMutation({
    mutationFn: () =>
      api.post<{ docNo: string; grandTotal: string; replaced?: string; advanceUsed?: string; advanceLeft?: string }>(
        correcting ? `/purchases/${correcting.id}/replace` : '/purchases',
        {
          ...(reentering && !correcting ? { reenterOf: reentering.id } : {}),
          branchId: header.branchId || defaultBranchId,
          docDate: header.docDate,
          supplierId: header.supplierId,
          ...(header.supplierInvoiceNo ? { supplierInvoiceNo: header.supplierInvoiceNo } : {}),
          ...(header.supplierInvoiceDate ? { supplierInvoiceDate: header.supplierInvoiceDate } : {}),
          purchaseType: header.purchaseType,
          isInterstate: header.isInterstate,
          ...(header.taxMode === 'PCT'
            ? header.taxPct
              ? { taxPct: header.taxPct }
              : {}
            : header.taxAmount
              ? { taxAmount: header.taxAmount }
              : {}),
          settlement: header.settlement,
          ...(advanceUsed > 0 ? { advanceUsed: advanceUsed.toFixed(2) } : {}),
          ...(header.locationId ? { locationId: header.locationId } : {}),
          items: lines.values.map((l) => ({
            productId: l.productId,
            // Both optional: a blank tag is generated, and a piece with no
            // barcode is found at the till by its tag number.
            ...(l.itemCode.trim() ? { itemCode: l.itemCode.trim() } : {}),
            ...(l.barcode.trim() ? { barcode: l.barcode.trim() } : {}),
            purityId: l.purityId,
            grossWt: l.grossWt,
            ...(l.stoneWt ? { stoneWt: l.stoneWt } : {}),
            // A flat figure if one was typed; otherwise the terms, and the
            // server works the cost out — the same figure every profit is
            // measured against.
            // Every term typed is sent, always. A typed cost still decides
            // what the piece cost — it is the supplier's own figure — but the
            // rate, wastage, making and stones behind it are kept with the
            // line, so they show again on Correct and in the reports. Sending
            // the cost alone used to drop them without a word.
            ...(l.cost ? { cost: l.cost } : {}),
            ...(l.ratePerGram ? { ratePerGram: l.ratePerGram, rateBasis: l.rateBasis } : {}),
            ...(l.wastageValue ? { wastage: { mode: l.wastageMode, value: l.wastageValue } } : {}),
            ...(l.makingValue ? { making: { mode: l.makingMode, value: l.makingValue } } : {}),
            ...(l.stoneAmount ? { stoneAmount: l.stoneAmount } : {}),
            ...(l.photo ? { imageDataUri: l.photo } : {}),
            ...(l.locationId ? { locationId: l.locationId } : {}),
          })),
        },
        // A retry after a timeout must not record the purchase twice.
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (result) => {
      toast(
        result.replaced ? `${result.replaced} corrected as ${result.docNo}` : `Purchase ${result.docNo} posted`,
        result.replaced
          ? `${result.replaced} is voided and ${result.docNo} posted in its place — ${money(result.grandTotal)}`
          : `${money(result.grandTotal)} — the pieces are in stock now${
              Number(result.advanceUsed ?? 0) > 0
                ? `. ${money(result.advanceUsed!)} came from their advance; ${money(result.advanceLeft ?? '0')} is still with them`
                : ''
            }`,
      );
      setCorrecting(null);
      setReentering(null);
      setOpen(false);
      lines.reset();
      setHeader({ ...header, supplierInvoiceNo: '', taxAmount: '', taxPct: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['purchases'] });
      void client.invalidateQueries({ queryKey: ['advances'] });
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const linesReady = lines.values.every(
    (l) => !!l.productId && !!l.purityId && !!l.grossWt && (!!l.cost || !!l.ratePerGram),
  );
  const ready = header.supplierId !== '' && lines.values.length > 0 && linesReady;

  /** Leaving the entry forgets any correction, so the next one is a plain new purchase. */
  const closeForm = (): void => {
    setOpen(false);
    setCorrecting(null);
    setReentering(null);
    setError(null);
  };
  const startNew = (): void => {
    if (correcting || reentering) lines.reset();
    setCorrecting(null);
    setReentering(null);
    setError(null);
    setOpen(true);
  };

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/purchases/${id}/cancel`, { reason: cancelReason.trim() }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['purchases'] });
      void client.invalidateQueries({ queryKey: ['items'] });
      toast(`${cancelling?.doc_no ?? 'Purchase'} voided`, 'The pieces are out of stock and the payable is reversed.');
      setCancelling(null);
      setCancelReason('');
    },
    onError: setError,
  });

  /**
   * Fill the entry from an existing purchase. Used two ways: to correct a
   * posted one (saving replaces it), and to re-enter a cancelled one (saving
   * posts it fresh; the cancelled one stays as it is, on the record).
   */
  const loadInto = useMutation({
    mutationFn: async ({ row }: { row: { id: string; doc_no: string }; replace: boolean }) =>
      api.get<PurchaseDetail>(`/purchases/${row.id}`),
    onSuccess: (detail, { row, replace }) => {
      setCorrectingAdvance({
        supplierId: replace ? (detail.supplier_id ?? '') : '',
        amount: replace ? Number(detail.advance_used ?? 0) : 0,
      });
      setHeader({
        supplierId: detail.supplier_id ?? '',
        docDate: String(detail.doc_date).slice(0, 10),
        supplierInvoiceNo: detail.supplier_invoice_no ?? '',
        supplierInvoiceDate: detail.supplier_invoice_date ? String(detail.supplier_invoice_date).slice(0, 10) : '',
        purchaseType: detail.purchase_type ?? 'JEWELLERY',
        settlement: detail.settlement ?? 'CREDIT',
        useAdvance: true,
        isInterstate: !!detail.is_interstate,
        // The GST as it was entered: a rate if a rate was typed, else the amount.
        taxAmount: !detail.tax_pct && Number(detail.tax_total ?? 0) > 0 ? String(detail.tax_total) : '',
        taxMode: detail.tax_pct ? 'PCT' : 'AMOUNT',
        taxPct: detail.tax_pct ?? '',
        branchId: detail.branch_id ?? '',
        locationId: '',
      });
      lines.replace(
        detail.lines.map((l) => {
          const t = l.calc_input ?? {};
          // Lines entered from a rate come back as that rate, wastage and
          // making, so a correction is made to the figure that was wrong
          // rather than to a lump-sum cost. Older lines with no terms kept
          // come back as the cost they were saved at.
          // A typed cost comes back as typed, alongside its terms.
          const fromTerms = !t.cost && !!t.ratePerGram;
          return {
            ...blankLine(),
            productId: l.product_id ?? '',
            purityId: l.purity_id,
            grossWt: l.gross_wt,
            stoneWt: Number(l.stone_wt) > 0 ? l.stone_wt : '',
            cost: fromTerms ? '' : (t.cost ?? l.line_total),
            ratePerGram: t.ratePerGram ?? '',
            rateBasis: t.rateBasis ?? 'NET',
            wastageMode: t.wastage?.mode ?? 'PCT',
            wastageValue: t.wastage?.value ?? '',
            makingMode: t.making?.mode ?? 'PER_GRAM',
            makingValue: t.making?.value ?? '',
            stoneAmount: t.stoneAmount ?? '',
            photo: t.imageDataUri ?? '',
            locationId: t.locationId ?? '',
          };
        }),
      );
      setCorrecting(replace ? row : null);
      setReentering(replace ? null : row);
      setError(null);
      setOpen(true);
    },
    onError: setError,
  });

  const allRows = list.data?.data ?? [];
  const cancelledCount = allRows.filter((p) => p.status === 'CANCELLED').length;
  // Cancelled bills are kept for the record but folded away, so a corrected or
  // re-entered purchase does not read as the same bill twice.
  const rows = showCancelled ? allRows : allRows.filter((p) => p.status !== 'CANCELLED');

  // The form itself, rendered as the page when a purchase is being entered.
  const purchaseForm = (
    <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-5 pb-24">
      <div className="card grid gap-3 md:grid-cols-4">
        <Field label="Supplier" required>
          <Select
            value={header.supplierId}
            onChange={(v) => setHeader({ ...header, supplierId: v })}
            options={supplierList.map((s) => ({ value: s.id, label: s.name }))}
            placeholder={supplierList.length === 0 ? 'Add a supplier first' : 'Choose…'}
          />
          <MasterLink to="/suppliers" label="New supplier" permission="supplier.create" />
        </Field>

        <Field label="Purchase date" required>
          <DateInput value={header.docDate} onChange={(v) => setHeader({ ...header, docDate: v })} />
        </Field>

        <Field label="Supplier invoice no.">
          <TextInput
            value={header.supplierInvoiceNo}
            onChange={(v) => setHeader({ ...header, supplierInvoiceNo: v })}
          />
        </Field>

        <Field label="Their invoice date">
          <DateInput
            value={header.supplierInvoiceDate}
            onChange={(v) => setHeader({ ...header, supplierInvoiceDate: v })}
          />
        </Field>

        <Field label="Type">
          <Select
            value={header.purchaseType}
            onChange={(v) => setHeader({ ...header, purchaseType: v })}
            options={[
              { value: 'JEWELLERY', label: 'Jewellery' },
              { value: 'BULLION', label: 'Bullion' },
              { value: 'LOOSE_METAL', label: 'Loose metal' },
              { value: 'STONE', label: 'Stones' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
        </Field>

        <Field label={advanceUsed > 0 ? 'Rest of the bill' : 'Settlement'}>
          <Select
            value={header.settlement}
            onChange={(v) => setHeader({ ...header, settlement: v })}
            options={[
              { value: 'CREDIT', label: 'On credit' },
              { value: 'CASH', label: 'Paid cash' },
              { value: 'BANK', label: 'Paid by bank' },
            ]}
          />
        </Field>

        {advanceHeld > 0 ? (
          <Field label="Supplier's advance" hint={`They hold ${money(advanceHeld.toFixed(2))} paid ahead.`}>
            <label className="flex h-[2.125rem] items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={header.useAdvance}
                onChange={(e) => setHeader({ ...header, useAdvance: e.target.checked })}
              />
              Use it on this bill
            </label>
          </Field>
        ) : null}

        {branches.length > 1 ? (
          <Field label="Branch" required>
            <Select
              value={header.branchId || (defaultBranchId ?? '')}
              onChange={(v) => setHeader({ ...header, branchId: v })}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
            />
          </Field>
        ) : null}

        <Field
          label="GST charged"
          hint={
            chosenSupplier && !chosenSupplier.is_registered
              ? 'This supplier is unregistered — input tax cannot be claimed.'
              : 'Total tax on their invoice.'
          }
        >
          {/* A supplier's bill states a rate far more often than a
                figure, so both are offered and the other is worked out. */}
          <div className="flex items-center gap-2">
            <NumberInput
              value={header.taxMode === 'PCT' ? header.taxPct : header.taxAmount}
              onChange={(v) =>
                setHeader(header.taxMode === 'PCT' ? { ...header, taxPct: v } : { ...header, taxAmount: v })
              }
              placeholder={header.taxMode === 'PCT' ? '3' : '0.00'}
              decimals={2}
            />
            <div className="flex overflow-hidden rounded-md border border-silver-100">
              {(['PCT', 'AMOUNT'] as const).map((m) => (
                <button
                  type="button"
                  key={m}
                  className={`px-2.5 py-1 text-sm ${
                    header.taxMode === m ? 'bg-velvet text-white' : 'bg-surface text-ink-soft'
                  }`}
                  onClick={() => setHeader({ ...header, taxMode: m })}
                >
                  {m === 'PCT' ? '%' : '₹'}
                </button>
              ))}
            </div>
            {header.taxMode === 'PCT' && Number(header.taxPct) > 0 ? (
              <span className="num text-2xs text-ink-soft">= {money(taxOnBill.toFixed(2))}</span>
            ) : null}
          </div>
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={header.isInterstate}
          onChange={(e) => setHeader({ ...header, isInterstate: e.target.checked })}
        />
        Inter-state purchase (IGST rather than CGST and SGST)
      </label>

      {/* Where the pieces go as they are unpacked. One place for the
            whole purchase, with any single piece overridable in its row. */}
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-silver-100 bg-surface px-3 py-2">
        <div className="w-64">
          <p className="label">Put these pieces in</p>
          <select
            className="field"
            value={header.locationId}
            onChange={(e) => setHeader({ ...header, locationId: e.target.value })}
          >
            <option value="">{locationOptions.length === 0 ? 'No locations set up yet' : 'Not placed yet'}</option>
            {locationOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <MasterLink to="/locations" label="New location" />
        </div>
        <p className="pb-1.5 text-2xs text-ink-faint">
          {locationOptions.length === 0
            ? 'Add showcases and safes under Locations to track where each piece is kept.'
            : 'Change a single row below if one piece goes somewhere else.'}
        </p>
      </div>

      <LineTable
        head={headings.map((h) => ({
          label: h.label,
          // Figures sit right, over the boxes they head; words and choices left.
          align: ['grossWt', 'stoneWt', 'netWt', 'rate', 'wastage', 'making', 'stoneAmount', 'cost'].includes(h.key)
            ? ('right' as const)
            : ('left' as const),
        }))}
        addLabel="Add piece"
        onAdd={lines.add}
        footer={
          <tfoot className="bg-gold-50/60">
            <tr>
              {/* The row number and every column before Cost, whichever are shown. */}
              <td className="td text-right text-2xs uppercase tracking-wide text-ink-soft" colSpan={beforeCost + 1}>
                What the pieces cost
              </td>
              <td className="td num text-right font-semibold">{money(itemTotal.toFixed(2))}</td>
              {/* The columns after Cost, and the remove button's. */}
              <td className="td" colSpan={afterCost + 1} />
            </tr>
          </tfoot>
        }
      >
        {lines.lines.map((line, index) => (
          <LineRow
            key={line.key}
            index={index}
            removable={lines.lines.length > 1}
            onRemove={() => lines.remove(line.key)}
          >
            {has('photo') ? (
              <td className="td">
                <PhotoCell
                  value={line.photo || null}
                  onChange={(uri) => lines.update(line.key, { photo: uri ?? '' })}
                />
              </td>
            ) : null}
            <td className="td">
              <div className="flex items-center gap-1">
                {/* The piece's photo, shown the moment a product is chosen so
                      the row is recognisable at a glance. */}
                {line.photo ? <img src={line.photo} alt="" className="h-8 w-8 shrink-0 rounded object-cover" /> : null}
                <CellSelect
                  width="w-40"
                  value={line.productId}
                  onChange={(v) => {
                    if (v === '__new__') {
                      setQuickFor(line.key);
                      return;
                    }
                    // A product knows what it is made of, so choosing one fills
                    // its purity — and brings its photo onto the piece.
                    const chosen = productList.find((p) => p.id === v);
                    const fallback = masters.puritiesFor(String(chosen?.metal_id ?? ''))[0]?.id ?? '';
                    lines.update(line.key, {
                      productId: v,
                      purityId: String(chosen?.default_purity_id ?? '') || fallback,
                      ...(chosen?.image_data_uri && !line.photo ? { photo: chosen.image_data_uri } : {}),
                    });
                  }}
                  options={[
                    { value: '__new__', label: '+ New product…' },
                    ...productList.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` })),
                  ]}
                  placeholder="Product"
                />
              </div>
            </td>
            {has('tag') ? (
              <td className="td">
                <CellInput
                  value={line.itemCode}
                  onChange={(v) => lines.update(line.key, { itemCode: v.toUpperCase() })}
                  placeholder="auto"
                />
              </td>
            ) : null}
            {has('barcode') ? (
              <td className="td">
                <CellInput
                  value={line.barcode}
                  onChange={(v) => lines.update(line.key, { barcode: v })}
                  placeholder="scan"
                />
              </td>
            ) : null}
            <td className="td">
              <CellSelect
                width="w-24"
                value={line.purityId}
                onChange={(v) => lines.update(line.key, { purityId: v })}
                options={purityOptionsFor(line.productId)}
              />
            </td>
            <td className="td">
              <CellInput
                numeric
                decimals={3}
                width="w-24"
                value={line.grossWt}
                onChange={(v) => lines.update(line.key, { grossWt: v })}
                placeholder="g"
              />
            </td>
            {has('stoneWt') ? (
              <td className="td">
                <CellInput
                  numeric
                  decimals={3}
                  width="w-24"
                  value={line.stoneWt}
                  onChange={(v) => lines.update(line.key, { stoneWt: v })}
                  placeholder="g"
                />
              </td>
            ) : null}
            {has('netWt') ? (
              <td className="td num text-right text-ink-soft">
                {line.grossWt ? (Number(line.grossWt) - Number(line.stoneWt || 0)).toFixed(3) : '—'}
              </td>
            ) : null}
            {/* The supplier's terms: a rate, then what they added on
                  top of it. Any of them may be left alone. */}
            {has('rate') ? (
              <td className="td">
                <div className="flex items-center justify-end gap-1">
                  <CellInput
                    numeric
                    decimals={2}
                    width="w-24"
                    value={line.ratePerGram}
                    onChange={(v) => lines.update(line.key, { ratePerGram: v })}
                    placeholder="₹/g"
                  />
                  <select
                    className="field w-[4.5rem] px-1 py-1 text-2xs"
                    value={line.rateBasis}
                    onChange={(e) => lines.update(line.key, { rateBasis: e.target.value as Line['rateBasis'] })}
                    title="Per gram of net weight, or per gram of pure metal"
                  >
                    <option value="NET">/g net</option>
                    <option value="FINE">/g fine</option>
                  </select>
                </div>
              </td>
            ) : null}

            {has('wastage') ? (
              <td className="td">
                <div className="flex items-center justify-end gap-1">
                  <CellInput
                    numeric
                    decimals={2}
                    width="w-20"
                    value={line.wastageValue}
                    onChange={(v) => lines.update(line.key, { wastageValue: v })}
                    placeholder="0"
                  />
                  <select
                    className="field w-14 px-1 py-1 text-2xs"
                    value={line.wastageMode}
                    onChange={(e) => lines.update(line.key, { wastageMode: e.target.value as Line['wastageMode'] })}
                  >
                    <option value="PCT">%</option>
                    <option value="AMOUNT">₹</option>
                  </select>
                </div>
              </td>
            ) : null}

            {has('making') ? (
              <td className="td">
                <div className="flex items-center justify-end gap-1">
                  <CellInput
                    numeric
                    decimals={2}
                    width="w-20"
                    value={line.makingValue}
                    onChange={(v) => lines.update(line.key, { makingValue: v })}
                    placeholder="0"
                  />
                  <select
                    className="field w-16 px-1 py-1 text-2xs"
                    value={line.makingMode}
                    onChange={(e) => lines.update(line.key, { makingMode: e.target.value as Line['makingMode'] })}
                  >
                    <option value="PER_GRAM">₹/g</option>
                    <option value="AMOUNT">₹</option>
                    <option value="PCT">%</option>
                  </select>
                </div>
              </td>
            ) : null}

            {has('stoneAmount') ? (
              <td className="td">
                <CellInput
                  numeric
                  decimals={2}
                  width="w-24"
                  value={line.stoneAmount}
                  onChange={(v) => lines.update(line.key, { stoneAmount: v })}
                  placeholder="0"
                />
              </td>
            ) : null}

            <td className="td">
              {/* Typed outright, or left blank and worked out from the
                    terms — the placeholder shows what it comes to. */}
              <CellInput
                numeric
                decimals={2}
                width="w-28"
                value={line.cost}
                onChange={(v) => lines.update(line.key, { cost: v })}
                placeholder={lineCost(line) > 0 ? lineCost(line).toFixed(2) : '₹'}
              />
            </td>

            {has('place') ? (
              <td className="td">
                <CellSelect
                  width="w-32"
                  value={line.locationId}
                  onChange={(v) => lines.update(line.key, { locationId: v })}
                  options={locationOptions}
                  placeholder="Same"
                />
              </td>
            ) : null}
          </LineRow>
        ))}
      </LineTable>

      <div className="flex items-end justify-between">
        <p className="max-w-md text-xs text-ink-soft">
          Net and fine weight are worked out by the server from the gross and stone weights, using the same engine that
          prices the sale.
        </p>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-ink-soft">
            {advanceUsed > 0 ? 'Bill total' : 'Payable to supplier'}
          </p>
          <p className={`num font-semibold ${advanceUsed > 0 ? 'text-lg' : 'text-2xl'}`}>
            {money(grandTotal.toFixed(2))}
          </p>
          {advanceUsed > 0 ? (
            <>
              <p className="num text-sm text-success">− {money(advanceUsed.toFixed(2))} from their advance</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-ink-soft">
                {header.settlement === 'CREDIT' ? 'Still owed to them' : 'To pay now'}
              </p>
              <p className="num text-2xl font-semibold">{money(afterAdvance.toFixed(2))}</p>
              <p className="text-2xs text-ink-soft">
                Advance left with them: {money((advanceHeld - advanceUsed).toFixed(2))}
              </p>
            </>
          ) : null}
        </div>
      </div>

      {/* The buttons follow the page down: on a long purchase the row
            was below the fold, so "Post purchase" could not be found
            without scrolling past every line. */}
      <div className="sticky bottom-0 z-10 -mx-1 mt-2 flex gap-2 border-t border-silver-100 bg-surface px-1 py-3">
        <button className="btn-primary" disabled={!ready || create.isPending}>
          {create.isPending ? 'Posting…' : 'Post purchase'}
        </button>
        <button type="button" className="btn-ghost" onClick={closeForm}>
          Cancel
        </button>
      </div>
    </form>
  );

  /**
   * Entering a purchase takes over the page rather than floating above it.
   *
   * A form with a dozen columns needs the whole width of the screen, and one
   * that hovers over the page it came from has to invent its own header, its
   * own scrolling and its own way out — none of which behave like the rest of
   * the app. This is the same page, showing the form instead of the list.
   */
  if (open) {
    return (
      <>
        <PageHeader
          title={
            correcting ? `Correct ${correcting.doc_no}` : reentering ? `Re-enter ${reentering.doc_no}` : 'New purchase'
          }
          subtitle="The cost you enter per piece is what profit is measured against, for as long as that piece is in stock."
          actions={
            <button className="btn-ghost" onClick={closeForm}>
              Back to purchases
            </button>
          }
        />
        <ErrorNotice error={error} />
        {correcting ? (
          <div className="mb-4 rounded-md border border-gold-300 bg-gold-50 px-3 py-2.5 text-sm">
            Correcting <span className="num font-semibold">{correcting.doc_no}</span>. Nothing has changed yet — when
            you save, it is voided and this one is posted in its place, both together. If the save fails,{' '}
            {correcting.doc_no} stays exactly as it was.
          </div>
        ) : null}
        {reentering ? (
          <div className="mb-4 rounded-md border border-gold-300 bg-gold-50 px-3 py-2.5 text-sm">
            Re-entering cancelled <span className="num font-semibold">{reentering.doc_no}</span>, filled in as it was.
            Saving posts it as a new purchase and links the two, so it is not counted twice.
          </div>
        ) : null}
        <RatePanel />
        {purchaseForm}
        {quickFor ? (
          <QuickProduct
            onCancel={() => setQuickFor(null)}
            onCreated={(prod) => {
              const fallback = masters.puritiesFor(prod.metal_id)[0]?.id ?? '';
              lines.update(quickFor, {
                productId: prod.id,
                purityId: prod.default_purity_id ?? fallback,
                ...(prod.image_data_uri ? { photo: prod.image_data_uri } : {}),
              });
              setQuickFor(null);
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Purchases"
        subtitle="Buying from a supplier. Each piece is tagged as it arrives, so it can be scanned at the counter the same day."
        actions={
          <button className="btn-primary" onClick={startNew}>
            New purchase
          </button>
        }
      />
      <ErrorNotice error={error} />

      {list.isLoading ? <Loading /> : null}
      {cancelledCount > 0 ? (
        <label className="mb-2 flex items-center justify-end gap-2 text-xs text-ink-soft">
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
          Show cancelled ({cancelledCount})
        </label>
      ) : null}
      {!list.isLoading && allRows.length === 0 ? (
        <Empty
          message="No purchases recorded yet."
          hint="Record what you have bought from a supplier and each piece is tagged as it arrives, ready to scan at the counter."
          action={{ label: 'New purchase', onClick: startNew }}
        />
      ) : (
        <Table head={['Date', 'Document', 'Type', 'Supplier', 'Pieces', 'Total', 'Status', '']}>
          {rows.map((p) => (
            <tr key={p.id}>
              <td className="td">{date(p.doc_date)}</td>
              <td className="td num">{p.doc_no}</td>
              <td className="td text-xs">{p.purchase_type.replace(/_/g, ' ').toLowerCase()}</td>
              <td className="td">{p.party_name}</td>
              <td className="td num text-right">{p.pieces}</td>
              <td className="td num text-right">{money(p.grand_total)}</td>
              <td className="td">
                {p.status.toLowerCase()}
                {p.replaced_by_doc_no ? (
                  <span className="block text-2xs text-ink-soft">→ re-entered as {p.replaced_by_doc_no}</span>
                ) : null}
                {p.replaces_doc_no ? (
                  <span className="block text-2xs text-ink-soft">in place of {p.replaces_doc_no}</span>
                ) : null}
              </td>
              <td className="td text-right">
                <Link className="mr-3 text-xs underline" to={`/purchases/${p.id}/print`}>
                  Print
                </Link>
                {p.status === 'POSTED' && can('document.void') ? (
                  <>
                    <button
                      className="mr-3 text-xs text-gold-700 underline"
                      disabled={loadInto.isPending}
                      onClick={() => loadInto.mutate({ row: p, replace: true })}
                    >
                      Correct
                    </button>
                    <button className="text-xs text-danger underline" onClick={() => setCancelling(p)}>
                      Void
                    </button>
                  </>
                ) : null}
                {p.status === 'CANCELLED' && !p.replaced_by_doc_no && can('purchase.post') ? (
                  <button
                    className="text-xs text-gold-700 underline"
                    title="Open this purchase filled in, to post it again"
                    disabled={loadInto.isPending}
                    onClick={() => loadInto.mutate({ row: p, replace: false })}
                  >
                    Re-enter
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      )}

      <ConfirmDialog
        open={!!cancelling}
        tone="danger"
        title={`Void ${cancelling?.doc_no ?? ''}?`}
        message="Every piece from this purchase leaves stock and the payable to the supplier is reversed, in a new dated entry that keeps the trail. This is only possible while no piece has been sold or moved. To fix a mistake instead, use Correct."
        detail={
          <label className="block text-sm">
            <span className="mb-1 block text-2xs text-ink-soft">Reason (kept on the record)</span>
            <input
              className="field w-full"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Wrong weight entered"
              autoComplete="off"
            />
          </label>
        }
        confirmLabel="Void the purchase"
        confirmDisabled={cancelReason.trim().length < 3}
        busy={cancel.isPending}
        onCancel={() => {
          setCancelling(null);
          setCancelReason('');
        }}
        onConfirm={() => cancelling && cancel.mutate(cancelling.id)}
      />
    </>
  );
}
